import { useEffect, useRef, useState } from 'react';
import { FONTS, HIGHLIGHT_COLORS } from '@td/shared';
import { uploadNoteImage } from '../hooks/useNotes';

/**
 * The writing surface for a note.
 *
 * Like the lesson-plan field it uses `contentEditable` with `execCommand`,
 * which is deprecated and still the only formatting API every browser agrees
 * on. Pulling in a full editor framework would be more code than the whole
 * notes feature. Whatever this produces is re-sanitised on the server before
 * it is stored — the allowlist there, not this component, is what makes the
 * markup safe.
 */

const TEXT_COLORS = [
  { value: '#4A3F35', label: 'Default' },
  { value: '#B07C63', label: 'Terracotta' },
  { value: '#9A6045', label: 'Rust' },
  { value: '#C08F3F', label: 'Ochre' },
  { value: '#7C8A5F', label: 'Olive' },
  { value: '#7887A0', label: 'Slate' },
  { value: '#9C7A93', label: 'Mauve' },
  { value: '#B07373', label: 'Rose' },
];

const SIZES = [
  { cmd: '2', label: 'Small' },
  { cmd: '3', label: 'Normal' },
  { cmd: '5', label: 'Large' },
  { cmd: '6', label: 'Huge' },
];

/**
 * A short list rather than all forty-eight from Settings. This is picking a
 * font for one paragraph, not for the whole app, and a menu that long inside
 * a toolbar is a menu nobody reads to the bottom of.
 */
const NOTE_FONTS = [
  'Nunito Sans',
  'Quicksand',
  'Poppins',
  'Comfortaa',
  'Montserrat',
  'Bebas Neue',
  'Fraunces',
  'Lora',
  'Merriweather',
  'Playfair Display',
  'Caveat',
  'Patrick Hand',
  'Indie Flower',
  'Fredoka',
  'Chewy',
  'Schoolbell',
  'Inter',
  'JetBrains Mono',
].filter((name) => FONTS.some((f) => f.name === name));

/** Enough for a teacher, without a grid of a thousand. */
const EMOJI = [
  '😀',
  '😊',
  '🙂',
  '😍',
  '🤔',
  '😅',
  '😴',
  '🤯',
  '🎉',
  '✨',
  '⭐',
  '🌟',
  '❤️',
  '💛',
  '💚',
  '💙',
  '💜',
  '🔥',
  '👍',
  '👏',
  '🙌',
  '🤝',
  '💪',
  '🧠',
  '👀',
  '📌',
  '📍',
  '📝',
  '✏️',
  '📖',
  '📚',
  '🎒',
  '🍎',
  '✅',
  '❌',
  '⚠️',
  '❗',
  '❓',
  '🔔',
  '⏰',
  '📅',
  '📆',
  '🗓️',
  '📊',
  '📈',
  '📉',
  '🏆',
  '🥇',
  '🎯',
  '🧩',
  '🎨',
  '🎵',
  '⚽',
  '🌈',
  '☀️',
  '🌧️',
  '❄️',
  '🌸',
  '🌻',
  '🌱',
];

export function NoteEditor({
  value,
  onChange,
  noteId,
  minHeight = 320,
}: {
  value: string;
  onChange: (html: string) => void;
  /** Ties uploaded pictures to this note, so deleting it cleans them up. */
  noteId?: string;
  minHeight?: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [menu, setMenu] = useState<'none' | 'color' | 'highlight' | 'font' | 'size' | 'emoji'>(
    'none',
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Only write into the DOM when the incoming value genuinely differs, or the
  // caret jumps to the start on every keystroke.
  useEffect(() => {
    const el = ref.current;
    if (el && el.innerHTML !== value) el.innerHTML = value || '';
  }, [value]);

  function emit() {
    onChange(ref.current?.innerHTML ?? '');
  }

  function exec(command: string, arg?: string) {
    ref.current?.focus();
    document.execCommand(
      'styleWithCSS',
      false,
      /*
       * These four have to emit CSS rather than tags. Left to itself the
       * browser writes <font size> and <font color>, and `font` is not on the
       * server's allowlist — the tag would be stripped on save and the
       * formatting would silently vanish the moment the note reloaded.
       */
      ['hiliteColor', 'foreColor', 'fontName', 'fontSize'].includes(command) ? 'true' : 'false',
    );
    document.execCommand(command, false, arg);
    setMenu('none');
    emit();
  }

  function insert(html: string) {
    ref.current?.focus();
    document.execCommand('insertHTML', false, html);
    setMenu('none');
    emit();
  }

  async function addImage(file: File) {
    setError(null);
    setBusy(true);
    try {
      const url = await uploadNoteImage(file, noteId);
      insert(`<img src="${url}" alt="" />`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "That image couldn't be added");
    } finally {
      setBusy(false);
    }
  }

  /** Pasting a screenshot should just work — that is how most images arrive. */
  async function onPaste(e: React.ClipboardEvent) {
    const file = [...e.clipboardData.items].find((i) => i.type.startsWith('image/'))?.getAsFile();

    if (file) {
      e.preventDefault();
      await addImage(file);
    }
  }

  return (
    <div>
      <div className="flex flex-wrap items-center gap-1 rounded-t-lg border border-b-0 border-sand bg-linen/70 px-1.5 py-1">
        <Tool onClick={() => exec('bold')} title="Bold">
          <b>B</b>
        </Tool>
        <Tool onClick={() => exec('italic')} title="Italic">
          <i>I</i>
        </Tool>
        <Tool onClick={() => exec('underline')} title="Underline">
          <u>U</u>
        </Tool>
        <Tool onClick={() => exec('strikeThrough')} title="Strike through">
          <s>S</s>
        </Tool>

        <Divider />

        <Menu
          label="Size"
          open={menu === 'size'}
          onToggle={() => setMenu(menu === 'size' ? 'none' : 'size')}
        >
          {SIZES.map((s) => (
            <MenuItem key={s.cmd} onClick={() => exec('fontSize', s.cmd)}>
              {s.label}
            </MenuItem>
          ))}
        </Menu>

        <Menu
          label="Font"
          open={menu === 'font'}
          onToggle={() => setMenu(menu === 'font' ? 'none' : 'font')}
        >
          {NOTE_FONTS.map((name) => (
            <MenuItem
              key={name}
              onClick={() => exec('fontName', name)}
              style={{ fontFamily: name }}
            >
              {name}
            </MenuItem>
          ))}
        </Menu>

        <Menu
          label="Colour"
          open={menu === 'color'}
          onToggle={() => setMenu(menu === 'color' ? 'none' : 'color')}
        >
          <div className="grid grid-cols-4 gap-1 p-1.5">
            {TEXT_COLORS.map((c) => (
              <button
                key={c.value}
                type="button"
                onClick={() => exec('foreColor', c.value)}
                title={c.label}
                aria-label={c.label}
                className="h-6 w-6 rounded border border-sand"
                style={{ backgroundColor: c.value }}
              />
            ))}
          </div>
        </Menu>

        <Menu
          label="Highlight"
          open={menu === 'highlight'}
          onToggle={() => setMenu(menu === 'highlight' ? 'none' : 'highlight')}
        >
          <div className="grid grid-cols-4 gap-1 p-1.5">
            {HIGHLIGHT_COLORS.map((c) => (
              <button
                key={c.value}
                type="button"
                onClick={() => exec('hiliteColor', c.value)}
                title={c.name}
                aria-label={c.name}
                className="h-6 w-6 rounded border border-sand"
                style={{ backgroundColor: c.value }}
              />
            ))}
            <button
              type="button"
              onClick={() => exec('hiliteColor', 'transparent')}
              title="No highlight"
              aria-label="No highlight"
              className="col-span-4 rounded border border-sand py-1 text-xs text-walnut hover:bg-linen"
            >
              None
            </button>
          </div>
        </Menu>

        <Divider />

        <Tool onClick={() => exec('formatBlock', '<h2>')} title="Heading">
          H
        </Tool>
        <Tool onClick={() => exec('insertUnorderedList')} title="Bulleted list">
          ☰
        </Tool>
        <Tool onClick={() => exec('insertOrderedList')} title="Numbered list">
          ⒈
        </Tool>
        <Tool onClick={() => exec('formatBlock', '<blockquote>')} title="Quote">
          ❝
        </Tool>
        <Tool onClick={() => insert('<hr />')} title="Divider line">
          —
        </Tool>

        <Divider />

        <Menu
          label="Emoji"
          open={menu === 'emoji'}
          onToggle={() => setMenu(menu === 'emoji' ? 'none' : 'emoji')}
        >
          <div className="grid max-h-52 grid-cols-10 gap-0.5 overflow-y-auto p-1.5">
            {EMOJI.map((e) => (
              <button
                key={e}
                type="button"
                onClick={() => insert(e)}
                className="rounded p-1 text-lg leading-none hover:bg-linen"
              >
                {e}
              </button>
            ))}
          </div>
        </Menu>

        {/* A label styled as a button — a bare file input cannot be styled. */}
        <label
          className={[
            'cursor-pointer rounded px-2 py-1 text-xs text-walnut transition-colors hover:bg-sand/60',
            busy ? 'pointer-events-none opacity-60' : '',
          ].join(' ')}
          title="Add a picture"
        >
          {busy ? 'Adding…' : 'Picture'}
          <input
            type="file"
            accept="image/*"
            className="sr-only"
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (file) void addImage(file);
            }}
          />
        </label>

        <Tool onClick={() => exec('removeFormat')} title="Clear formatting">
          ⌫
        </Tool>
      </div>

      <div
        ref={ref}
        contentEditable
        role="textbox"
        aria-multiline="true"
        aria-label="Note"
        onInput={emit}
        onBlur={emit}
        onPaste={(e) => void onPaste(e)}
        suppressContentEditableWarning
        className="note-body rounded-b-lg border border-sand bg-white px-4 py-3 text-sm leading-relaxed text-espresso focus:border-clay focus:outline-none"
        style={{ minHeight }}
      />

      {error && <p className="mt-1 text-xs text-rust">{error}</p>}
      <p className="mt-1 text-xs text-stone">
        Paste a screenshot straight in, or use Picture for a file.
      </p>
    </div>
  );
}

function Tool({
  onClick,
  title,
  children,
}: {
  onClick: () => void;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      // Mouse-down rather than click: clicking a toolbar button would
      // otherwise blur the editor and throw away the selection first.
      onMouseDown={(e) => {
        e.preventDefault();
        onClick();
      }}
      title={title}
      aria-label={title}
      className="min-w-[28px] rounded px-2 py-1 text-xs text-walnut transition-colors hover:bg-sand/60"
    >
      {children}
    </button>
  );
}

function Divider() {
  return <span className="mx-0.5 h-4 w-px bg-sand" aria-hidden />;
}

function Menu({
  label,
  open,
  onToggle,
  children,
}: {
  label: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <span className="relative">
      <button
        type="button"
        onMouseDown={(e) => {
          e.preventDefault();
          onToggle();
        }}
        aria-expanded={open}
        className="rounded px-2 py-1 text-xs text-walnut transition-colors hover:bg-sand/60"
      >
        {label} ▾
      </button>

      {open && (
        <div className="absolute left-0 top-full z-20 mt-1 min-w-[9rem] rounded-lg border border-sand bg-white py-1 shadow-lifted">
          {children}
        </div>
      )}
    </span>
  );
}

function MenuItem({
  onClick,
  style,
  children,
}: {
  onClick: () => void;
  style?: React.CSSProperties;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onMouseDown={(e) => {
        e.preventDefault();
        onClick();
      }}
      style={style}
      className="block w-full px-3 py-1.5 text-left text-sm text-espresso hover:bg-linen"
    >
      {children}
    </button>
  );
}
